import { chatService } from '../chatService';
import {
  ChatClientOp,
  ChatServerOp,
  ChatSocketStatus,
  type ChatServerFrame,
  type ChatSyncResult,
  type ChatWsTicket,
} from '@/types/chat';

/** API Gateway drops a socket after 10 idle minutes; a ping every 5 keeps it alive. */
export const CHAT_PING_INTERVAL_MS = 5 * 60_000;
export const CHAT_RECONNECT_BASE_MS = 1_000;
export const CHAT_RECONNECT_MAX_MS = 30_000;
/** Connect tickets live this long on the server; `expiresAt - TTL` is the server's clock at mint. */
const CHAT_TICKET_TTL_MS = 60_000;

type FrameListener = (frame: ChatServerFrame) => void;
type StatusListener = (status: ChatSocketStatus) => void;
type SyncListener = (result: ChatSyncResult) => void;

export interface ChatSocketDeps {
  getUrl: () => string | undefined;
  createTicket: () => Promise<ChatWsTicket>;
  getSync: (since: string) => Promise<ChatSyncResult>;
  /** Injected for tests; defaults to Math.random. */
  random?: () => number;
}

/** Server-clock time the ticket was minted, so `/sync?since=` doesn't depend on this device's clock. */
function mintedAt(ticket: ChatWsTicket): string {
  const expires = Date.parse(ticket.expiresAt);
  return new Date(
    Number.isNaN(expires) ? Date.now() : expires - CHAT_TICKET_TTL_MS,
  ).toISOString();
}

function isServerFrame(value: unknown): value is ChatServerFrame {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as { op?: unknown }).op === 'string'
  );
}

/**
 * The one app-wide chat socket. Every connect — first or reconnect — uses a fresh single-use
 * ticket, so the JWT never goes in a URL. A dropped socket reconnects with exponential backoff
 * plus jitter (capped at 30 s), then asks `/chat/sync` what changed while it was away and tells
 * subscribers. Chat screens say which conversation they are showing with `open` / `close`.
 */
export class ChatSocket {
  private ws: WebSocket | null = null;
  private active = false;
  private status = ChatSocketStatus.IDLE;
  private attempt = 0;
  /** Bumped on stop and on every connect, so a ticket that resolves late is discarded. */
  private generation = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private openConversationId: string | null = null;
  /** Server-clock time of the last successful connect; `null` until the first one. */
  private lastConnectedAt: string | null = null;
  private readonly frameListeners = new Set<FrameListener>();
  private readonly statusListeners = new Set<StatusListener>();
  private readonly syncListeners = new Set<SyncListener>();
  private readonly random: () => number;

  constructor(private readonly deps: ChatSocketDeps) {
    this.random = deps.random ?? Math.random;
  }

  /** Open the socket (after login). Safe to call repeatedly. */
  start(): void {
    if (this.active) return;
    this.active = true;
    this.attempt = 0;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
    }
    void this.connect();
  }

  /** Close the socket and forget the session (logout). */
  stop(): void {
    if (!this.active && this.status === ChatSocketStatus.IDLE) return;
    this.active = false;
    this.generation += 1;
    this.clearReconnect();
    this.stopPing();
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
    }
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000);
    this.openConversationId = null;
    this.lastConnectedAt = null;
    this.setStatus(ChatSocketStatus.IDLE);
  }

  /** The chat screen for `conversationId` is visible. Re-sent automatically after a reconnect. */
  open(conversationId: string): void {
    this.openConversationId = conversationId;
    this.send({ action: ChatClientOp.OPEN, conversationId });
  }

  /** The chat screen lost focus. Ignored when another conversation has been opened since. */
  close(conversationId?: string): void {
    if (conversationId && conversationId !== this.openConversationId) return;
    this.openConversationId = null;
    this.send({ action: ChatClientOp.CLOSE });
  }

  /** The conversation a chat screen is showing, if any. */
  getOpenConversationId(): string | null {
    return this.openConversationId;
  }

  getStatus(): ChatSocketStatus {
    return this.status;
  }

  subscribe(listener: FrameListener): () => void {
    this.frameListeners.add(listener);
    return () => this.frameListeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /** Fires after a reconnect with the conversations that changed while the socket was down. */
  onSync(listener: SyncListener): () => void {
    this.syncListeners.add(listener);
    return () => this.syncListeners.delete(listener);
  }

  private readonly handleOnline = () => {
    if (!this.active || this.ws) return;
    this.clearReconnect();
    this.attempt = 0;
    void this.connect();
  };

  private async connect(): Promise<void> {
    const generation = ++this.generation;
    this.setStatus(
      this.lastConnectedAt
        ? ChatSocketStatus.RECONNECTING
        : ChatSocketStatus.CONNECTING,
    );

    let ticket: ChatWsTicket;
    try {
      ticket = await this.deps.createTicket();
    } catch {
      if (generation === this.generation && this.active) {
        this.scheduleReconnect();
      }
      return;
    }
    if (generation !== this.generation || !this.active) return;

    const base = this.deps.getUrl();
    if (!base) {
      this.setStatus(ChatSocketStatus.IDLE);
      return;
    }

    const separator = base.includes('?') ? '&' : '?';
    const ws = new WebSocket(
      `${base}${separator}ticket=${encodeURIComponent(ticket.ticket)}`,
    );
    this.ws = ws;
    const connectedAt = mintedAt(ticket);

    ws.onopen = () => {
      if (this.ws !== ws) return;
      const since = this.lastConnectedAt;
      this.lastConnectedAt = connectedAt;
      this.attempt = 0;
      this.startPing();
      if (this.openConversationId) {
        this.send({
          action: ChatClientOp.OPEN,
          conversationId: this.openConversationId,
        });
      }
      this.setStatus(ChatSocketStatus.OPEN);
      if (since) void this.runSync(since);
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.ws !== ws) return;
      let frame: unknown;
      try {
        frame = JSON.parse(String(event.data));
      } catch {
        return;
      }
      if (!isServerFrame(frame)) return;
      if (frame.op === ChatServerOp.RECONNECT) {
        this.reconnectNow();
        return;
      }
      for (const listener of [...this.frameListeners]) listener(frame);
    };

    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.stopPing();
      if (this.active) this.scheduleReconnect();
    };

    // An error is always followed by `close`, which owns the reconnect.
    ws.onerror = () => undefined;
  }

  /** The server asked for a fresh connection: drop this one without backoff. */
  private reconnectNow(): void {
    const ws = this.ws;
    this.ws = null;
    this.stopPing();
    ws?.close(1000);
    this.clearReconnect();
    this.attempt = 0;
    if (this.active) void this.connect();
  }

  private scheduleReconnect(): void {
    this.clearReconnect();
    const ceiling = Math.min(
      CHAT_RECONNECT_MAX_MS,
      CHAT_RECONNECT_BASE_MS * 2 ** this.attempt,
    );
    // Equal jitter: half the ceiling plus a random share of the other half, so a fleet of clients
    // dropped together doesn't reconnect in lockstep.
    const delay = ceiling / 2 + (this.random() * ceiling) / 2;
    this.attempt += 1;
    this.setStatus(ChatSocketStatus.RECONNECTING);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private async runSync(since: string): Promise<void> {
    try {
      const result = await this.deps.getSync(since);
      if (!this.active) return;
      for (const listener of [...this.syncListeners]) listener(result);
    } catch {
      // Best-effort: open chats also catch up on their own when the socket reopens.
    }
  }

  private send(frame: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(
      () => this.send({ action: ChatClientOp.PING }),
      CHAT_PING_INTERVAL_MS,
    );
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private setStatus(status: ChatSocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of [...this.statusListeners]) listener(status);
  }
}

export const chatSocket = new ChatSocket({
  getUrl: () => import.meta.env.VITE_APP_SOCKET_URL,
  createTicket: chatService.createWsTicket,
  getSync: chatService.getSync,
});
